import { RadioButtonWithLabelOption, TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { Messages } from '../../../../codegen/Localize';
import { FormikRadioButtonGroup } from '../../../../commonInfra/components/FormikRadioButtonGroup';
import { HelpService } from '../../../../commonInfra/services/HelpService';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { learnMoreStyle, settingsRadioButtonItemHeight, settingsRadioButtonLastItemIndent, SettingsStyles } from '../../../styles/CommonStyles';
import { AskDataMode } from '../../../types/AskDataMode';
import { SiteSettingsForSiteAdminProps } from '../ReactSiteSettingsForSiteAdmin';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export const ASK_DATA_SECTION_TEST_ID = 'ask-data-site-setting';
export const ASK_DATA_LEARN_MORE_TEST_ID = 'ask-data-learn-more';
export const ASK_DATA_MODE_ENABLED_BUTTON_TEST_ID = 'ask-data-mode-enabled';
export const ASK_DATA_MODE_DISABLED_BUTTON_TEST_ID = 'ask-data-mode-disabled';
export const ASK_DATA_MODE_ALWAYS_DISABLED_BUTTON_TEST_ID = 'ask-data-mode-disabled-always';

export type AskDataSectionProps = SiteSettingsForSiteAdminProps<'askDataMode'>;

export function AskDataSection(props: AskDataSectionProps) {
  const formik = useFormikContext<AskDataSectionProps>();
  const { buttonList, className } = React.useMemo(() => getButtonListAndClassName(formik.values), [formik.values]);
  return (
    <SettingsSection
      title={Messages.nlpSwitchSiteSettingTitle()}
      description={getDescription()}
      testId={ASK_DATA_SECTION_TEST_ID}
    >
      <div className={SettingsStyles.settingsGroupDescription}>
        {Messages.nlpSwitchDescriptionPartTwo()}
      </div>
      <FormikRadioButtonGroup
        aria-label={Messages.nlpSwitchSiteSettingTitle()}
        buttonList={buttonList}
        className={className}
        name={'settings.askDataMode'}
      />
    </SettingsSection>
  );
}

function getDescription() {
  return (
    <span>
      {Messages.nlpSwitchDescriptionPartOne()}
      <TextLinkWidget
        className={learnMoreStyle}
        href={HelpService.getServerHelpUrlByCategory('ask-data-site-setting')}
        target='_blank'
        testId={ASK_DATA_LEARN_MORE_TEST_ID}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </span>
  );
}

function getButtonListAndClassName(props: AskDataSectionProps) {
  const askDataModeEnabledButtonLabel = <>
    <div>{Messages.AskData_enabledByDefault()}</div>
    <div>{Messages.nlpEnabledDescription()}</div>
  </>;
  const askDataModeDisabledButtonLabel = <>
    <div>{Messages.AskData_disabledByDefault()}</div>
    <div>{Messages.nlpDisabledDescription()}</div>
  </>;
  const askDataModeAlwaysDisabledButtonLabel = <>
    <div>{Messages.AskData_alwaysDisabled()}</div>
    <div>{Messages.nlpDisabledAlwaysDescription()}</div>
  </>;

  let buttonList: RadioButtonWithLabelOption<AskDataMode>[] = [
    {
      value: AskDataMode.ENABLED_BY_DEFAULT,
      label: askDataModeEnabledButtonLabel,
      testId: ASK_DATA_MODE_ENABLED_BUTTON_TEST_ID
    },
    {
      value: AskDataMode.DISABLED_BY_DEFAULT,
      label: askDataModeDisabledButtonLabel,
      testId: ASK_DATA_MODE_DISABLED_BUTTON_TEST_ID
    }
  ];
  let className: string = settingsRadioButtonItemHeight;

  if (props.settings.askDataMode !== AskDataMode.ENABLED_BY_DEFAULT) {
    buttonList.push({
      value: AskDataMode.DISABLED_ALWAYS,
      label: askDataModeAlwaysDisabledButtonLabel,
      testId: ASK_DATA_MODE_ALWAYS_DISABLED_BUTTON_TEST_ID
    });
    className = `${settingsRadioButtonItemHeight} ${settingsRadioButtonLastItemIndent}`;
  }
  return { buttonList, className };
}

/* for registering the component in settings registry */
export const AskDataSectionSpecs = componentRegistryEntry({
  name: 'AskDataSection',
  component: AskDataSection
});
